import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { removeFunnel, removeMetadataElement } from '../../reducers/united-canvas/unitedCanvasReducer';
import { useConfigurationCanvas } from '../../components/pages/configuration-canvas/public/useConfigurationCanvas';
import { useFunnelsApiFacade } from '../../apis/facades/useFunnelsApiFacade';

export const useResetHealthcareFunnels = () => {
  const dispatch = useDispatch();
  const { canvasFunnelMap } = useConfigurationCanvas();
  const { delete_funnel } = useFunnelsApiFacade();

  const resetHealthcareFunnels = useCallback(async () => {
    console.log('🔄 Resetting healthcare demo - deleting all healthcare funnels and removing all metadata elements');
    
    if (!canvasFunnelMap?.funnelsData) {
      console.warn('No funnel map data available');
      return;
    }

    // Find the healthcare funnels created in quick1
    const newPatientIntakeFunnel = Object.values(canvasFunnelMap.funnelsData).find(
      (funnel) => funnel.name.startsWith('New Patient Intake')
    );

    const activePatientCareFunnel = Object.values(canvasFunnelMap.funnelsData).find(
      (funnel) => funnel.name.startsWith('Active Patient Care')
    );

    let deletedCount = 0;

    // Delete "New Patient Intake" funnel if found
    if (newPatientIntakeFunnel) {
      console.log('Deleting New Patient Intake funnel from backend:', newPatientIntakeFunnel.id);
      try {
        await delete_funnel(newPatientIntakeFunnel.id);
        dispatch(removeFunnel({ funnelId: newPatientIntakeFunnel.id }));
        deletedCount++;
        console.log('✅ New Patient Intake funnel deleted successfully');
      } catch (error) {
        console.error('❌ Failed to delete New Patient Intake funnel:', error);
      }
    } else {
      console.log('New Patient Intake funnel not found (may already be deleted)');
    }

    // Delete "Active Patient Care" funnel if found
    if (activePatientCareFunnel) {
      console.log('Deleting Active Patient Care funnel from backend:', activePatientCareFunnel.id);
      try {
        await delete_funnel(activePatientCareFunnel.id);
        dispatch(removeFunnel({ funnelId: activePatientCareFunnel.id }));
        deletedCount++;
        console.log('✅ Active Patient Care funnel deleted successfully');
      } catch (error) {
        console.error('❌ Failed to delete Active Patient Care funnel:', error);
      }
    } else {
      console.log('Active Patient Care funnel not found (may already be deleted)');
    }

    // Remove ALL metadata elements from the canvas
    let removedMetadataCount = 0;
    if (canvasFunnelMap.metadataElements && Object.keys(canvasFunnelMap.metadataElements).length > 0) {
      console.log('🧹 Removing all metadata elements from canvas...');
      
      const metadataElementIds = Object.keys(canvasFunnelMap.metadataElements);
      console.log(`Found ${metadataElementIds.length} metadata element(s) to remove:`, metadataElementIds);
      
      metadataElementIds.forEach((fullMetadataId) => {
        console.log(`Removing metadata element: ${fullMetadataId}`);
        dispatch(removeMetadataElement({ fullMetadataId }));
        removedMetadataCount++;
      });
      
      console.log(`✅ Successfully removed ${removedMetadataCount} metadata element(s) from canvas`);
    } else {
      console.log('No metadata elements found on canvas to remove');
    }

    if (deletedCount > 0 || removedMetadataCount > 0) {
      console.log(`🔄 Reset complete! Deleted ${deletedCount} healthcare funnel(s) from backend and canvas, and removed ${removedMetadataCount} metadata element(s)`);
    } else {
      console.log('🔄 No healthcare funnels or metadata elements found to delete');
    }
  }, [dispatch, canvasFunnelMap, delete_funnel]);

  return { resetHealthcareFunnels };
};
